import { EMPLOYEES } from "./workforce-employees";
import type { EmployeeSlug, Employee } from "./workforce-employees";

/* PEOPLE workforce data: the eight AI employees, the superagent, traces and departments. The eight
 * are ported verbatim from the PEOPLE site: the wording is the brand's, so change it only against
 * that source (`assets/content.js`). The live site must never read this. */

export * from "./workforce-employees";

export interface TraceStep {
  t: string;
  /** Sub-line under the step. */
  s?: string;
  /** Marks a step a person takes, not the AI employee. */
  human?: boolean;
}

export interface Trace {
  who: EmployeeSlug;
  instruction: string;
  steps: TraceStep[];
  outcome: string;
  /* The source carries this as one string with an inline <b>; split so React
     renders it without dangerouslySetInnerHTML. */
  foot: { lead: string; body: string };
}

export const TRACES: Trace[] = [
  {
    who: "amy",
    instruction:
      "New family lead received. Qualify the inquiry, schedule a tour and collect move-in documents.",
    steps: [
      { t: "Inquiry answered and qualified" },
      { t: "Tour booked, family kept warm" },
      { t: "Admission-ready handoff on the record" },
    ],
    outcome: "Family qualified, tour booked",
    foot: {
      lead: "Stays human:",
      body: "the tour, the care decision, the move-in.",
    },
  },
  {
    who: "olivia",
    instruction: "Hire a care attendant. Contractor to start.",
    steps: [
      { t: "Role posted where candidates are" },
      { t: "Applicants screened and confirmed" },
      { t: "Interviews on your calendar" },
    ],
    outcome: "Interviews scheduled",
    foot: { lead: "Next human touchpoint:", body: "meet the candidates." },
  },
  {
    who: "david",
    instruction: "PTAC not cooling. Room 214: reported 7:12 AM.",
    steps: [
      { t: "Work order read in full context" },
      { t: "Repair options priced" },
      { t: "Cart ready to approve" },
    ],
    outcome: "One ticket, priced paths, one approved order",
    foot: {
      lead: "Stays human:",
      body: "the diagnosis on site, and approving the spend.",
    },
  },
  {
    who: "sophia",
    instruction:
      "Resident fall reported. 911 called. Document the incident and prepare staff follow-up.",
    steps: [
      { t: "Incident documented as it happened" },
      { t: "Chart and dispositions updated" },
      { t: "Follow-up ready for staff" },
    ],
    outcome: "Family informed by staff, record complete",
    foot: {
      lead: "Stays human:",
      body: "emergency response, care and reassurance.",
    },
  },
  {
    who: "sarah",
    instruction: "Take over the front desk line for the weekend.",
    steps: [
      { t: "Every call answered, all weekend" },
      { t: "Requests routed with full context" },
      { t: "Monday digest ready" },
    ],
    outcome: "Every call answered, nothing waiting Monday",
    foot: {
      lead: "Stays human:",
      body: "the conversation a family needs a person for, Sarah hands off warm.",
    },
  },
  {
    who: "nancy",
    instruction: "We're short on incontinence supplies. Restock this month, within budget.",
    steps: [
      { t: "Need sized from real usage" },
      { t: "Best compliant price found" },
      { t: "Order staged for approval" },
    ],
    outcome: "One cart, Best price, on the record",
    foot: { lead: "Stays human:", body: "approving the spend." },
  },
  {
    who: "james",
    instruction: "Close out the month. Flag anything that doesn't reconcile.",
    steps: [
      { t: "Invoices generated from the record" },
      { t: "Everything reconciled, discrepancies surfaced" },
      { t: "Statements sent on schedule" },
    ],
    outcome: "Month closed, no weekend lost",
    foot: {
      lead: "Stays human:",
      body: "the judgment call on every correction.",
    },
  },
  {
    who: "michael",
    instruction: "The survey window opens next month. Get us ready.",
    steps: [
      { t: "Every record audited" },
      { t: "Gaps routed and fixes verified" },
      { t: "Evidence assembled" },
    ],
    outcome: "Survey-ready with proof on the record",
    foot: {
      lead: "Stays human:",
      body: "the survey itself: and the standards you set.",
    },
  },
];

export const EMPLOYEE_BY_SLUG = Object.fromEntries(EMPLOYEES.map((e) => [e.slug, e])) as Record<
  EmployeeSlug,
  Employee
>;

/* Route params arrive as arbitrary strings, so /workforce/:slug narrows here
   before it can index EMPLOYEE_BY_SLUG. */

export function isEmployeeSlug(v: string): v is EmployeeSlug {
  return Object.prototype.hasOwnProperty.call(EMPLOYEE_BY_SLUG, v);
}

/* Empty for the superagent: Aria runs no desk. Callers render an "at work" section only when the
 * result is non-empty; a heading over an empty rail reads as a failed page. */

export function tracesFor(slug: EmployeeSlug): Trace[] {
  return TRACES.filter((t) => t.who === slug);
}
